import { BILLING_INTERVAL, BillingInterval, IActiveSubscription, IPlan, PLAN_TYPE } from '@/types';

/**
 * Get the correct price for a plan based on billing cycle
 *
 * BACKWARD COMPATIBLE PRICING LOGIC:
 * - NEW plans: Use explicit annualPrice/annualDiscountedPrice from DB
 * - OLD plans: Fallback to monthly * 10 for backward compatibility
 *
 * Priority for Annual Pricing (when showOriginalPrice = false):
 *   1. annualDiscountedPrice (if exists and > 0)
 *   2. annualPrice (if exists and > 0)
 *   3. discountedPrice * 10 (if exists and > 0)
 *   4. price * 10 (fallback)
 *
 * Priority for Annual Original Price (when showOriginalPrice = true):
 *   1. annualPrice (if exists and > 0)
 *   2. price * 10 (fallback)
 *
 * @param plan - The plan object from API
 * @param billingCycle - BILLING_INTERVAL.MONTH or BILLING_INTERVAL.YEAR
 * @param showOriginalPrice - true = show strikethrough price (before discount), false = show display price (after discount)
 * @returns The calculated price as a number
 *
 * @example
 * // Old plan (no annual fields) - Annual display
 * getPlanPrice({price: 30, discountedPrice: 28}, BILLING_INTERVAL.YEAR, false)
 * // Returns: 280 (28 * 10)
 *
 * @example
 * // New plan with annual prices - Annual display
 * getPlanPrice({price: 30, discountedPrice: 28, annualPrice: 300, annualDiscountedPrice: 280}, BILLING_INTERVAL.YEAR, false)
 * // Returns: 280 (from annualDiscountedPrice)
 *
 * @example
 * // Monthly pricing - Display
 * getPlanPrice({price: 30, discountedPrice: 28}, BILLING_INTERVAL.MONTH, false)
 * // Returns: 28 (discountedPrice)
 */
export const getPlanPrice = (
  plan: IPlan,
  billingCycle: BillingInterval,
  showOriginalPrice = false
): number => {
  if (billingCycle === BILLING_INTERVAL.YEAR) {
    if (showOriginalPrice) {
      // For strikethrough original price (before discount)
      // 1. Try explicit annual base price (NEW plans)
      if (plan.annualPrice && plan.annualPrice > 0) {
        return plan.annualPrice;
      }
      // 2. Fallback to monthly * 10 (OLD plans)
      return (plan.price || 0) * 10;
    } else {
      // For display price (discounted if available)
      // 1. Try annual discounted price first (PRIORITY - NEW plans)
      if (plan.annualDiscountedPrice && plan.annualDiscountedPrice > 0) {
        return plan.annualDiscountedPrice;
      }
      // 2. Try annual base price (NEW plans without discount)
      if (plan.annualPrice && plan.annualPrice > 0) {
        return plan.annualPrice;
      }
      // 3. Fallback to monthly discounted * 10 (OLD plans with discount)
      if (plan.discountedPrice && plan.discountedPrice > 0) {
        return plan.discountedPrice * 10;
      }
      // 4. Final fallback to monthly base * 10 (OLD plans without discount)
      return (plan.price || 0) * 10;
    }
  }

  // Monthly pricing (unchanged)
  if (showOriginalPrice) {
    // Original monthly price (for strikethrough)
    return plan.price || 0;
  } else {
    // Display monthly price (discounted if available)
    return plan.discountedPrice || plan.price || 0;
  }
};

/**
 * Get the display name for a user's current plan.
 *
 * During the 14-day free trial on the Starter (FREE) plan, returns "Starter".
 * After trial ends (or for existing users with null trialEnd), returns the plan name as-is.
 * Non-Starter plans pass through unchanged.
 * If subscription is fully expired (CANCELED + cancelAt in past), returns "Starter".
 */
export const getDisplayPlanName = (
  subscription?: Pick<IActiveSubscription, 'plan' | 'trialEnd' | 'status' | 'cancelAt'> | null
): string => {
  if (!subscription?.plan) return 'Starter';

  // If subscription is fully expired, user is effectively on Starter
  if (
    subscription.status === 'CANCELED' &&
    subscription.cancelAt &&
    new Date(subscription.cancelAt) < new Date()
  ) {
    return 'Starter';
  }

  const planName = subscription.plan.name === 'Free' ? 'Starter' : subscription.plan.name;

  if (
    subscription.plan.type === PLAN_TYPE.FREE &&
    subscription.trialEnd &&
    new Date(subscription.trialEnd) > new Date()
  ) {
    return 'Starter';
  }

  return planName;
};

/**
 * Returns true when the user's subscription is over in Stripe and cannot be
 * revived — so every plan, including Starter, must be reachable through a fresh
 * checkout rather than a retry or a plan change.
 *
 * Per REQ-219 acceptance criteria, frozen accounts must be treated as having
 * no active plan: no plan card is marked as "current", and every paid plan is
 * selectable so the user can convert to any tier through a fresh checkout.
 *
 * Matches two statuses, both of which mean Stripe has terminated the
 * subscription — there is no live subscription left to duplicate:
 *
 *   INCOMPLETE_EXPIRED — the trial expired (or an INCOMPLETE checkout was
 *     abandoned) without a payment method. Stripe voids the latest invoice, and
 *     the "Resubscribe" path (`/subscription/retry-payment/:id`) calls
 *     `stripe.invoices.pay()`, which only succeeds on `open` invoices — so
 *     retry always fails for this status.
 *
 *   EXPIRED — written only when `customer.subscription.deleted` (or an explicit
 *     cancellation reaching its end date) finalizes the row. There is no grace
 *     period in this state: a subscription still inside its cancel-at-period-end
 *     window is CANCELED with a future `cancelAt`, not EXPIRED.
 *
 * Why EXPIRED is safe to include, given an earlier widening was reverted in
 * review: that attempt added CANCELED, which `/subscription/me/active`
 * deliberately returns during the grace period — so a fresh checkout there would
 * create a *second* live subscription. EXPIRED is never returned by
 * `/me/active`; it only ever reaches the page through the delinquent fallback in
 * `/me/display`, which by construction means the user has no active subscription
 * at all. PricingPlans additionally re-checks `activeSubscription` for
 * ACTIVE/TRIALING inside this branch and routes those through the plan-change
 * API instead of checkout, so the duplicate path stays closed either way.
 *
 * Deliberately NOT matched: PAST_DUE and UNPAID. Those are live subscriptions in
 * dunning — Stripe is still retrying, and a fresh checkout would duplicate them.
 *
 * NOTE: before the 2026-08-16 data repair, ~907 rows were PAST_DUE with `endedAt`
 * set — genuinely over, but wearing a dunning status. Those rows are excluded
 * here on purpose; they are fixed in the data (see
 * `apps/api/scripts/backfill-regressed-expired-subscriptions.ts`), not papered over here,
 * because matching PAST_DUE would also catch users who really are in dunning.
 */
export const isFrozenAccount = (
  subscription?: Pick<IActiveSubscription, 'status'> | null
): boolean => subscription?.status === 'INCOMPLETE_EXPIRED' || subscription?.status === 'EXPIRED';
